import { useToast } from 'components/ToastContainer';
import useProduct from 'hooks/useProduct';
import { useEffect, useMemo, useRef, useState } from 'react';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import { Product } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import useQuery from 'hooks/useQuery';
import { isEqual } from 'lodash';

const formatToUUID = (hex: string): string => {
  return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(
    12,
    16
  )}-${hex.substring(16, 20)}-${hex.substring(20)}`;
};

const useProductCarrier = () => {
  const toast = useToast();
  const { getMasterListProduct, getProduct } = useProduct();

  const {
    utilityState: { selectedCarrier }
  } = useAgOperationUtility();

  // Fetch master list product
  const {
    data: masterListProductData,
    isLoading: isLoadingMasterListProduct
  } = useQuery<Product>(selectedCarrier ?? '', getMasterListProduct, {
    cacheKeyPrefix: 'masterListProduct',
    cacheTTL: 0
  });

  // Fetch inventory product
  const { data: inventoryProductData, isLoading: isLoadingProduct } = useQuery<
    Product
  >(selectedCarrier ?? '', getProduct, {
    cacheKeyPrefix: 'inventoryProduct',
    cacheTTL: 0
  });

  const { averagePricePerUnit, averagePricePerUnitUnit } =
    inventoryProductData ?? {};

  const isLoadingCarrier = isLoadingMasterListProduct || isLoadingProduct;

  // Track whether a fetch cycle has completed at least once, so we don't
  // misinterpret the initial pre-fetch render (isLoading starts as false) as a failure.
  const hasLoadedOnceRef = useRef(false);
  if (isLoadingCarrier) {
    hasLoadedOnceRef.current = true;
  }

  const [loadedCarrier, setLoadedCarrier] = useState<Product | null>(null);

  useEffect(() => {
    let nextLoadedCarrier: Product | null = null;

    if (!isLoadingCarrier) {
      if (masterListProductData?.id) {
        nextLoadedCarrier = {
          ...masterListProductData,
          ...(averagePricePerUnit != null && averagePricePerUnitUnit != null
            ? { averagePricePerUnit, averagePricePerUnitUnit }
            : {})
        } as Product;
      }

      setLoadedCarrier(prevLoadedCarrier =>
        isEqual(prevLoadedCarrier, nextLoadedCarrier)
          ? prevLoadedCarrier
          : nextLoadedCarrier
      );
    }
  }, [
    isLoadingCarrier,
    masterListProductData,
    averagePricePerUnit,
    averagePricePerUnitUnit
  ]);

  // Handle API error with carrier fetch.
  // useQuery errors are unreliable here because useProduct.js catches errors
  // internally and returns null (predates useQuery by 3 years). Instead, detect
  // failure by checking: a carrier was requested, loading finished, but no data arrived.
  const carrierFetchFailed =
    hasLoadedOnceRef.current &&
    !isLoadingCarrier &&
    !!selectedCarrier &&
    !masterListProductData?.id;

  useEffect(() => {
    if (carrierFetchFailed) {
      toast.error(LayoutStrings.products.tankInfo.selectCarrierErrorTitle, {
        supportButton: true,
        content: LayoutStrings.products.tankInfo.selectCarrierErrorContent
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierFetchFailed]);

  const formattedLoadedCarrier = useMemo(() => {
    if (!loadedCarrier?.id) {
      return null;
    }

    return {
      ...loadedCarrier,
      id: formatToUUID(loadedCarrier.id),
      isCarrier: true
    } as Product;
  }, [loadedCarrier]);

  return {
    loadedCarrier: formattedLoadedCarrier,
    isLoading: isLoadingCarrier,
    hasSettledWithoutCarrier:
      hasLoadedOnceRef.current && !isLoadingCarrier && !formattedLoadedCarrier
  };
};

export default useProductCarrier;
